/**
 * Delete a Tru-Stride video and every row that would keep it listed after refresh.
 *
 * Supabase RLS can return 200 with 0 rows deleted. Always verify the videos
 * row actually disappeared before treating the click as success.
 */

export function storagePathsForVideo(video) {
  const paths = [];
  if (video?.file_url) {
    const markers = ['/object/public/videos/', '/object/videos/', '/videos/'];
    for (const marker of markers) {
      const idx = video.file_url.indexOf(marker);
      if (idx !== -1) {
        paths.push(decodeURIComponent(video.file_url.slice(idx + marker.length)));
        break;
      }
    }
  }
  if (video?.id) {
    paths.push(`labeled/${video.id}_dlc_labeled.mp4`);
  }
  return [...new Set(paths.filter(Boolean))];
}

export function isMissingRelation(error) {
  if (!error) return false;
  const code = String(error.code || '');
  const message = String(error.message || '').toLowerCase();
  return (
    code === '42P01' ||
    code === 'PGRST205' ||
    message.includes('does not exist') ||
    message.includes('could not find the table') ||
    message.includes('schema cache')
  );
}

export function isMissingRpc(error) {
  if (!error) return false;
  const code = String(error.code || '');
  const message = String(error.message || '').toLowerCase();
  return (
    code === 'PGRST202' ||
    code === '42883' ||
    (message.includes('delete_video') && message.includes('not find')) ||
    message.includes('function public.delete_video') ||
    message.includes('could not find the function')
  );
}

const RELATED_TABLES = [
  'body_pattern_ticks',
  'user_feedback',
  'training_labels',
  'training_data_consent',
  'analysis_reports',
];

async function removeVideoStorage(supabase, video) {
  const paths = storagePathsForVideo(video);
  if (paths.length === 0) return;
  try {
    await supabase.storage.from('videos').remove(paths);
  } catch (_) {
    // Storage cleanup is best-effort. The database row is what the UI lists.
  }
}

async function deleteRelatedRows(supabase, videoId) {
  for (const table of RELATED_TABLES) {
    const { error } = await supabase.from(table).delete().eq('video_id', videoId);
    if (error && !isMissingRelation(error)) {
      throw new Error(`${table}: ${error.message}`);
    }
  }
}

export async function deleteVideoRecord(supabase, video) {
  if (!video?.id) {
    throw new Error('Missing video id');
  }

  const { data: rpcData, error: rpcError } = await supabase.rpc('delete_video', {
    p_video_id: video.id,
  });

  if (!rpcError) {
    await removeVideoStorage(supabase, video);
    if (rpcData && rpcData.ok === false) {
      throw new Error(rpcData.error || 'Delete failed');
    }
    return { id: video.id, via: 'rpc' };
  }

  if (!isMissingRpc(rpcError)) {
    throw new Error(rpcError.message || 'Delete failed');
  }

  await deleteRelatedRows(supabase, video.id);
  await removeVideoStorage(supabase, video);

  const { data, error } = await supabase
    .from('videos')
    .delete()
    .eq('id', video.id)
    .select('id');

  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error(
      'The video was not deleted. Database permissions blocked the row, so it is still there after refresh.'
    );
  }

  return { id: data[0].id, via: 'direct' };
}
